// src/pages/AdminDashboard.js - DASHBOARD ADMIN AVEC KPIs
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { Link } from 'react-router-dom';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [profsBlockes, setProfsBlockes] = useState([]);

  // 🔄 Charger les statistiques
  const chargerStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        setError('Vous devez être connecté comme admin');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/dashboard-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          setError('Accès admin requis');
          return;
        }
        throw new Error(`Erreur: ${response.status}`);
      }

      const data = await response.json();
      setStats(data.stats);
      setLastUpdate(new Date());

    } catch (err) {
      console.error('❌ Erreur chargement stats:', err);
      setError('Erreur de chargement: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

useEffect(() => {
    // Détecter les profs sans profil — via le backend (service_role) pour ne plus exposer auth.users
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/profs-sans-profil`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const json = await res.json();
        if (json?.success) setProfsBlockes(json.profs || []);
      } catch (e) {}
    })();
  }, []);

  useEffect(() => {
    // Attendre que la session soit prête
    const initAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        chargerStats();
      } else {
        // Écouter le changement d'auth
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          if (session) {
            chargerStats();
          }
        });
        return () => subscription.unsubscribe();
      }
    };
    
    initAuth();
    
    // Rafraîchir toutes les 5 minutes
    const interval = setInterval(chargerStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [chargerStats]);

  // 📊 Composant carte statistique
  const StatCard = ({ title, value, subtitle, icon, color = '#667eea', trend = null }) => (
    <div className="stat-card" style={{ borderLeftColor: color }}>
      <div className="stat-icon" style={{ backgroundColor: `${color}20`, color }}>
        {icon}
      </div>
      <div className="stat-content">
        <div className="stat-value">{value}</div>
        <div className="stat-title">{title}</div>
        {subtitle && <div className="stat-subtitle">{subtitle}</div>}
        {trend !== null && (
          <div className={`stat-trend ${trend >= 0 ? 'positive' : 'negative'}`}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)} cette semaine
          </div>
        )}
      </div>
    </div>
  );

  // 🚨 Composant alerte
  const AlertCard = ({ title, count, icon, link, color = '#ef4444' }) => (
    <Link to={link} className="alert-card" style={{ borderColor: count > 0 ? color : '#e5e7eb' }}>
      <div className="alert-icon" style={{ color: count > 0 ? color : '#9ca3af' }}>
        {icon}
      </div>
      <div className="alert-content">
        <div className="alert-count" style={{ color: count > 0 ? color : '#9ca3af' }}>{count}</div>
        <div className="alert-title">{title}</div>
      </div>
      {count > 0 && <div className="alert-badge">!</div>}
    </Link>
  );

  if (loading && !stats) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <p>{error}</p>
          <button onClick={chargerStats} className="retry-btn">🔄 Réessayer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <h1>📊 Dashboard Admin</h1>
          <p className="last-update">
            Dernière mise à jour: {lastUpdate?.toLocaleTimeString('fr-FR')}
          </p>
        </div>
        <div className="header-right">
          <button onClick={chargerStats} className="refresh-btn" disabled={loading}>
            {loading ? '⏳' : '🔄'} Actualiser
          </button>
        </div>
      </header>

      {/* Alerte profs bloqués */}
      {profsBlockes.length > 0 && (
        <section className="alerts-section" style={{marginBottom: 16}}>
          <h2>⚠️ Profs inscrits sans profil ({profsBlockes.length})</h2>
          <div style={{background:'#fffbeb', border:'1px solid #f59e0b', borderRadius:8, padding:'12px 16px'}}>
            <p style={{margin:'0 0 8px', fontSize:13, color:'#92400e', fontWeight:600}}>
              Ces profs ont confirmé leur email et se sont connectés mais leur profil n'a pas été créé (localStorage perdu). Contactez-les pour qu'ils recommencent l'inscription.
            </p>
            {profsBlockes.map(p => (
              <div key={p.user_id} style={{display:'flex', justifyContent:'space-between', padding:'6px 0', borderTop:'1px solid #fde68a', fontSize:13}}>
                <span style={{color:'#374151'}}>{p.email}</span>
                <span style={{color:'#6b7280', fontSize:12}}>
                  Inscrit le {new Date(p.created_at).toLocaleDateString('fr-FR')}
                  {p.last_sign_in_at ? ' · Connecté' : ' · Jamais connecté'}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Alertes */}
      {stats?.alertes?.total > 0 && (
        <section className="alerts-section">
          <h2>🚨 Alertes ({stats.alertes.total})</h2>
          <div className="alerts-grid">
            <AlertCard 
              title="Paiements incomplets" 
              count={stats.alertes.paiements_incomplete} 
              icon="💳" 
              link="/admin/paiements"
              color="#ef4444"
            />
            <AlertCard 
              title="Profs sans RIB" 
              count={stats.alertes.profs_sans_rib} 
              icon="🏦" 
              link="/admin/candidatures"
              color="#f59e0b"
            />
            <AlertCard 
              title="Cours non confirmés (+24h)" 
              count={stats.alertes.cours_non_confirmes_24h} 
              icon="⏰" 
              link="/admin/candidatures"
              color="#f97316"
            />
            <AlertCard 
              title="Candidatures en attente" 
              count={stats.alertes.candidatures_en_attente} 
              icon="👤" 
              link="/admin/candidatures"
              color="#8b5cf6"
            />
          </div>
        </section>
      )}

      {/* KPIs principaux */}
      <section className="kpi-section">
        <h2>📈 Vue d'ensemble</h2>
        <div className="kpi-grid">
          <StatCard 
            title="Élèves inscrits" 
            value={stats?.eleves?.total || 0}
            subtitle={`+${stats?.eleves?.ce_mois || 0} ce mois`}
            icon="👨‍🎓"
            color="#10b981"
            trend={stats?.eleves?.cette_semaine}
          />
          <StatCard 
            title="Profs validés" 
            value={stats?.profs?.valides || 0}
            subtitle={`${stats?.profs?.en_attente || 0} en attente`}
            icon="👨‍🏫"
            color="#3b82f6"
          />
          <StatCard 
            title="Cours à venir" 
            value={stats?.cours_a_venir || 0}
            subtitle={`${stats?.reservations?.confirmes || 0} confirmés au total`}
            icon="📚"
            color="#8b5cf6"
          />
          <StatCard 
            title="Revenus ce mois" 
            value={`${stats?.revenus?.ce_mois || 0}€`}
            subtitle={`Total: ${stats?.revenus?.total || 0}€`}
            icon="💰"
            color="#f59e0b"
          />
        </div>
      </section>

      {/* Détails Utilisateurs */}
      <section className="details-section">
        <div className="details-grid">
          {/* Élèves */}
          <div className="details-card">
            <h3>👨‍🎓 Élèves</h3>
            <div className="details-stats">
              <div className="detail-row">
                <span>Total</span>
                <span className="detail-value">{stats?.eleves?.total || 0}</span>
              </div>
              <div className="detail-row">
                <span>Aujourd'hui</span>
                <span className="detail-value highlight">{stats?.eleves?.aujourd_hui || 0}</span>
              </div>
              <div className="detail-row">
                <span>Cette semaine</span>
                <span className="detail-value">{stats?.eleves?.cette_semaine || 0}</span>
              </div>
              <div className="detail-row">
                <span>Ce mois</span>
                <span className="detail-value">{stats?.eleves?.ce_mois || 0}</span>
              </div>
            </div>
          </div>

          {/* Profs */}
          <div className="details-card">
            <h3>👨‍🏫 Professeurs</h3>
            <div className="details-stats">
              <div className="detail-row">
                <span>Total</span>
                <span className="detail-value">{stats?.profs?.total || 0}</span>
              </div>
              <div className="detail-row">
                <span>Validés</span>
                <span className="detail-value success">{stats?.profs?.valides || 0}</span>
              </div>
              <div className="detail-row">
                <span>En attente</span>
                <span className="detail-value warning">{stats?.profs?.en_attente || 0}</span>
              </div>
              <div className="detail-row">
                <span>Refusés</span>
                <span className="detail-value danger">{stats?.profs?.refuses || 0}</span>
              </div>
            </div>
            <Link to="/admin/candidatures" className="details-link">
              Gérer les candidatures →
            </Link>
          </div>

          {/* Réservations */}
          <div className="details-card">
            <h3>📅 Réservations</h3>
            <div className="details-stats">
              <div className="detail-row">
                <span>Total</span>
                <span className="detail-value">{stats?.reservations?.total || 0}</span>
              </div>
              <div className="detail-row">
                <span>Confirmées</span>
                <span className="detail-value success">{stats?.reservations?.confirmes || 0}</span>
              </div>
              <div className="detail-row">
                <span>En attente</span>
                <span className="detail-value warning">{stats?.reservations?.en_attente || 0}</span>
              </div>
              <div className="detail-row">
                <span>Annulées</span>
                <span className="detail-value danger">{stats?.reservations?.annules || 0}</span>
              </div>
            </div>
          </div>

          {/* Abonnements */}
          <div className="details-card">
            <h3>💳 Abonnements</h3>
            <div className="details-stats">
              <div className="detail-row">
                <span>Total</span>
                <span className="detail-value">{stats?.abonnements?.total || 0}</span>
              </div>
              <div className="detail-row">
                <span>Actifs</span>
                <span className="detail-value success">{stats?.abonnements?.actifs || 0}</span>
              </div>
              <div className="detail-row">
                <span>Essais</span>
                <span className="detail-value">{stats?.abonnements?.essais || 0}</span>
              </div>
              <div className="detail-row">
                <span>Mensuels</span>
                <span className="detail-value">{stats?.abonnements?.abonnements_mensuels || 0}</span>
              </div>
            </div>
            <Link to="/admin/paiements" className="details-link">
              Gérer les paiements →
            </Link>
          </div>
        </div>
      </section>

      {/* Évaluations */}
      <section className="reviews-section">
        <h2>⭐ Évaluations</h2>
        <div className="reviews-stats">
          <div className="review-stat">
            <span className="review-value">{stats?.reviews?.total || 0}</span>
            <span className="review-label">Avis total</span>
          </div>
          <div className="review-stat highlight">
            <span className="review-value">{stats?.reviews?.note_moyenne || 0}/5</span>
            <span className="review-label">Note moyenne</span>
          </div>
          <div className="review-stat">
            <span className="review-value">{stats?.reviews?.ce_mois || 0}</span>
            <span className="review-label">Ce mois</span>
          </div>
        </div>
      </section>

      {/* Navigation rapide */}
      <section className="quick-nav">
        <h2>🔗 Navigation rapide</h2>
        <div className="nav-links">
          <Link to="/admin/candidatures" className="nav-link">
            <span className="nav-icon">👤</span>
            <span>Candidatures</span>
          </Link>
         <Link to="/admin/messages" className="nav-link">
            <span className="nav-icon">📧</span>
            <span>Messages contact</span>
          </Link> 
          <Link to="/admin/monitoring" className="nav-link">
            <span className="nav-icon">📊</span>
            <span>Monitoring</span>
          </Link>
          <Link to="/admin/session-quality" className="nav-link">
            <span className="nav-icon">🎯</span>
            <span>Qualité sessions</span>
          </Link> 
          <Link to="/admin/paiements" className="nav-link">
            <span className="nav-icon">💰</span>
            <span>Paiements profs</span>
          </Link>
          <Link to="/professeurs" className="nav-link">
            <span className="nav-icon">👨‍🏫</span>
            <span>Liste des profs</span>
          </Link>
          <Link to="/dashboard" className="nav-link">
            <span className="nav-icon">🏠</span>
            <span>Dashboard élève</span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
